import { getApiBaseUrl, getRuntimeConfig } from '../config/runtimeConfig';
import { sessionFetch, updateSession } from './session';


async function request(path, options = {}) {
  let response;
  try {
    response = await sessionFetch(`${getApiBaseUrl()}${path}`, {
      credentials: 'include',
      ...options,
      headers: { Accept: 'application/json', ...options.headers },
      signal: AbortSignal.timeout(20000),
    });
  } catch (error) {
    throw new Error(error.name === 'TimeoutError'
      ? 'The request timed out. Please try again.'
      : path === '/auth/me' && options.method === 'PUT'
        ? 'Unable to connect to the server. Please make sure the backend is running and try again.'
        : 'Unable to connect. Please check your connection and try again.');
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    if (path === '/auth/me' && options.method === 'PUT' && response.status === 404) {
      const error = new Error('Profile updates are unavailable on the connected server. Please restart or update the backend and try again.');
      error.status = 404;
      throw error;
    }
    if (path !== '/auth/login' && (path !== '/auth/me' || options.method === 'PUT')) {
      const error = new Error(response.status >= 500 ? 'The request could not be completed. Please try again later.' : data?.message || 'Unable to complete the request.');
      error.status = response.status;
      throw error;
    }
    if (response.status === 401) {
      const error = new Error(path === '/auth/login' ? 'Email or password is incorrect. Please try again.' : 'Unauthorized');
      error.status = 401;
      throw error;
    }
    if (response.status === 429) throw new Error('Too many attempts. Please wait a moment and try again.');
    throw new Error(response.status >= 500
      ? 'Sign in is temporarily unavailable. Please try again later.' : typeof data?.message === 'string' ? data.message : 'Unable to sign in. Please try again.');
  }
  return data;
}

export async function login({ email, password }) {
  const session = await request('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return acceptSession(session);
}

async function acceptSession(session) {
  const user = session?.user?.id && Number.isFinite(session.expiresAt)
    ? { ...session.user, sessionExpiresAt: session.expiresAt }
    : await getSessionUser();
  updateSession(user);
  return user;
}

export async function googleLogin({ code }) {
  if (!code) throw new Error('Google did not return an authorization code. Please try again.');
  return acceptSession(await request('/auth/google', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ code }),
  }));
}

export function getLoginDestination() {
  const configured = getRuntimeConfig().AUTH_REDIRECT_PATH;
  if (!configured) return null;
  let destination;
  try { destination = new URL(configured, window.location.origin); } catch { return null; }
  if (destination.origin !== window.location.origin || ['/', '/login'].includes(destination.pathname.replace(/\/$/, '') || '/')) return null;
  return destination.pathname + destination.search + destination.hash;
}

export function changePassword({ currentPassword, newPassword }) {
  return request('/auth/change-password', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function logout() {
  const result = await request('/auth/logout', { method: 'POST' });
  updateSession(null);
  return result;
}

export async function updatePersonalInfo(values) {
  const session = await request('/auth/me', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: values.name, email: values.email, mobile: values.mobile }),
  });
  return acceptSession(session);
}

export async function getSessionUser() {
  const session = await request('/auth/me');
  if (!session?.user?.id) throw new Error('Your session could not be verified.');
  return { ...session.user, sessionExpiresAt: session.expiresAt };
}

export async function updateInventorySettings(level) {
  return acceptSession(await request('/auth/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inventory_measurement_level: level }) }));
}
